import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { training } from '../../lib/api';
import { confirmDialog } from '../../utils/dialogs';
import { cloneDeep, mergeDeep, formatMessage } from '../../utils/i18n';
import { toWeekStart, formatDateTimeDisplay, formatTimeRange, combineDateAndMinutes } from '../../utils/dateUtils';
import {
    TRAINING_SLOT_DURATION_MINUTES,
    TRAINING_TIME_SLOTS,
    sessionOverlapsSlot,
    sessionStartsInSlot,
} from '../../utils/trainingCalendar';

const DEFAULT_SCHEDULE_COPY = {
    header: {
        title: 'Schedule Training',
    },
    types: {
        skill: 'Skill Training',
        road: 'Road Training',
        skillFull: 'Skill Training',
        roadFull: 'Road Training',
    },
    weekNav: {
        previous: '← Previous Week',
        next: 'Next Week →',
    },
        booking: {
            heading: {
                skill: 'Book Skill Training',
                road: 'Book Road Training',
            },
            selectedTime: 'Selected Time: {time}',
            confirm: 'Confirm Booking',
            booking: 'Booking...',
        },
        table: {
            time: 'Time',
            yourSession: 'Your Session',
            reserved: 'Reserved',
            closed: 'Closed',
            buttons: {
                selected: 'Selected',
                book: 'Book',
                approved: 'Approved',
            },
        },
    session: {
        detailsHeading: 'Session Details',
        closeDetailsAria: 'Close details',
        studentLabel: 'Student',
        typeLabel: 'Type',
        timeLabel: 'Time',
        truckLabel: 'Truck',
        assignedByInstructor: 'Assigned by Instructor',
        statusLabel: 'Status',
        cancelConfirm: 'Are you sure you want to cancel this session?',
        cancelButton: 'Cancel Session',
        cancellingButton: 'Cancelling...',
        cancelNotice: 'Cancellations must be made at least 24 hours in advance.',
    },
    statuses: {
        PENDING: { label: 'Pending Approval', bookingNote: 'Pending Approval' },
        APPROVED: { label: 'Approved' },
        REJECTED: { label: 'Rejected' },
        CANCELLED: { label: 'Cancelled' },
    },
    messages: {
        booked: 'Session booked for {time}. Status: {status}',
        cancelSuccess: 'Session cancelled successfully',
        noTrucks: 'No trucks available for training at this time.',
        noTrucksForType: 'No trucks available for {type}.',
    },
    errors: {
        loadTrucks: 'Failed to load trucks. Please try again.',
        loadSessions: 'Failed to load sessions. Please try again.',
        pastSlot: 'Cannot book sessions in the past.',
        sunday: 'Training is not available on Sundays.',
        slotApproved: 'This slot is already approved for another student.',
        slotBooked: 'This slot is already booked.',
        selectTruck: 'Please select a truck first.',
        overlapApproved: 'This session overlaps with an approved session.',
        overlap: 'This session overlaps with another booking.',
        ownOverlap: 'You already have a session during this time.',
        skillLimit: 'You can only have 2 active skill training sessions per day.',
        roadLimit: 'You can only have 1 active road training session per day.',
        noSlot: 'Please select a time slot.',
        bookingFailed: 'Failed to book session. Please try again.',
        cancelFailed: 'Failed to cancel session.',
    },
};

export default function TrainingScheduleModal({ user, onClose, copy: copyOverrides }) {
    const copy = useMemo(() => mergeDeep(cloneDeep(DEFAULT_SCHEDULE_COPY), copyOverrides || {}), [copyOverrides]);
    const pendingStatusNote = copy.statuses?.PENDING?.bookingNote || DEFAULT_SCHEDULE_COPY.statuses.PENDING.bookingNote;
    const [weekStart, setWeekStart] = useState(toWeekStart(new Date()));
    const [sessions, setSessions] = useState([]);
    const [trucks, setTrucks] = useState([]);
    const [loadingSessions, setLoadingSessions] = useState(false);
    const [loadingTrucks, setLoadingTrucks] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [selectedTruckId, setSelectedTruckId] = useState(null);
    const [bookingLoading, setBookingLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showSessionDetails, setShowSessionDetails] = useState(false);
    const [focusedSession, setFocusedSession] = useState(null);
    const [sessionActionLoading, setSessionActionLoading] = useState(false);

    const weekDays = useMemo(() => (
        Array.from({ length: 7 }, (_, i) => {
            const day = new Date(weekStart);
            day.setDate(weekStart.getDate() + i);
            return day;
        })
    ), [weekStart]);

    const resolveTruckType = useCallback((truck) => {
        if (!truck) return 'SKILL';

        const explicitRaw = typeof truck.trainingType === 'string' ? truck.trainingType : '';
        const explicit = explicitRaw.trim().toUpperCase();
        if (explicit === 'SKILL' || explicit === 'ROAD') {
            return explicit;
        }

        if (explicit.includes('ROAD')) return 'ROAD';
        if (explicit.includes('SKILL')) return 'SKILL';

        const nameValue = String(truck.name || '').trim().toUpperCase();
        if (nameValue.includes('ROAD')) return 'ROAD';
        if (nameValue.includes('SKILL')) return 'SKILL';

        return 'SKILL';
    }, []);

    useEffect(() => {
        const loadTrucks = async () => {
            try {
                setLoadingTrucks(true);
                const data = await training.getTrucks();
                setTrucks(data);
            } catch (err) {
                console.error('Failed to load trucks:', err);
                setError(copy.errors?.loadTrucks || DEFAULT_SCHEDULE_COPY.errors.loadTrucks);
            } finally {
                setLoadingTrucks(false);
            }
        };
        loadTrucks();
    }, [copy.errors?.loadTrucks]);

    const refreshSessions = useCallback(async () => {
        try {
            setLoadingSessions(true);
            const weekIso = weekStart.toISOString();
            const [skill, road] = await Promise.all([
                training.getSessions('SKILL', weekIso).catch(() => []),
                training.getSessions('ROAD', weekIso).catch(() => []),
            ]);
            const combined = [...skill, ...road]
                .map(item => ({
                    ...item,
                    start: new Date(item.start),
                    end: new Date(item.end),
                }))
                .sort((a, b) => a.start.getTime() - b.start.getTime());
            setSessions(combined);
        } catch (err) {
            console.error('Failed to load sessions:', err);
            setError(copy.errors?.loadSessions || DEFAULT_SCHEDULE_COPY.errors.loadSessions);
        } finally {
            setLoadingSessions(false);
        }
    }, [weekStart, copy.errors?.loadSessions]);

    useEffect(() => {
        refreshSessions();
        setSelectedSlot(null);
        setSuccess('');
        setError('');
    }, [refreshSessions]);

    useEffect(() => {
        if (trucks.length === 0) {
            setSelectedTruckId(null);
            return;
        }
        if (!trucks.some(truck => truck.id === selectedTruckId)) {
            setSelectedTruckId(trucks[0].id);
        }
    }, [trucks, selectedTruckId]);

    const findSessionForSlot = useCallback((slotStart, truckId = null) => (
        sessions.find(session => {
            const matchesTime = sessionOverlapsSlot(session, slotStart);
            const matchesTruck = truckId ? session.truckId === truckId : true;
            return matchesTime && matchesTruck;
        })
    ), [sessions]);

    const findApprovedSessionForSlot = useCallback((slotStart, truckId = null) => (
        sessions.find(session => {
            const matchesTime = sessionOverlapsSlot(session, slotStart);
            const matchesTruck = truckId ? session.truckId === truckId : true;
            const isApproved = session.status === 'APPROVED';
            return matchesTime && matchesTruck && isApproved;
        })
    ), [sessions]);

    const getStatusLabel = useCallback((key) => (
        copy.statuses?.[key]?.label || DEFAULT_SCHEDULE_COPY.statuses[key]?.label || key
    ), [copy.statuses]);

    const getSessionStatusMeta = useCallback((status) => {
        const normalized = (status || '').toUpperCase();
        const label = getStatusLabel(normalized);
        switch (normalized) {
            case 'APPROVED':
                return { icon: '✅', label };
            case 'REJECTED':
                return { icon: '❌', label };
            case 'CANCELLED':
                return { icon: '🚫', label };
            case 'PENDING':
            default:
                return { icon: '⏳', label };
        }
    }, [getStatusLabel]);

    const getStatusBadgeClasses = (status) => {
        const normalized = (status || '').toUpperCase();
        switch (normalized) {
            case 'APPROVED':
                return 'bg-green-100 text-green-700 border border-green-200';
            case 'PENDING':
                return 'bg-yellow-100 text-yellow-700 border border-yellow-200';
            case 'REJECTED':
                return 'bg-red-100 text-red-700 border border-red-200';
            case 'CANCELLED':
                return 'bg-gray-100 text-gray-700 border border-gray-200';
            default:
                return 'bg-slate-100 text-slate-600 border border-slate-200';
        }
    };

    const handleSessionClick = (session) => {
        if (!session) return;
        setSuccess('');
        setError('');
        setFocusedSession(session);
        setShowSessionDetails(true);
    };

    const handleCancelSession = async (sessionId) => {
        if (!sessionId) return;
        try {
            setSessionActionLoading(true);
            setError('');
            await training.cancelSession(sessionId);
            await refreshSessions();
            setSuccess(copy.messages?.cancelSuccess || DEFAULT_SCHEDULE_COPY.messages.cancelSuccess);
            setShowSessionDetails(false);
            setFocusedSession(null);
        } catch (err) {
            console.error('Failed to cancel session:', err);
            setError(err?.message || copy.errors?.cancelFailed || DEFAULT_SCHEDULE_COPY.errors.cancelFailed);
        } finally {
            setSessionActionLoading(false);
        }
    };

    const validateSlot = (slotDate, truck = null) => {
        const bookingTruckId = truck?.id ?? selectedTruckId;
        const bookingType = resolveTruckType(truck || trucks.find(item => item.id === bookingTruckId));
        const now = new Date();
        if (!slotDate) return copy.errors?.noSlot || DEFAULT_SCHEDULE_COPY.errors.noSlot;
        if (slotDate < now) return copy.errors?.pastSlot || DEFAULT_SCHEDULE_COPY.errors.pastSlot;
        if (slotDate.getDay() === 0) return copy.errors?.sunday || DEFAULT_SCHEDULE_COPY.errors.sunday;
        if (!bookingTruckId) return copy.errors?.selectTruck || DEFAULT_SCHEDULE_COPY.errors.selectTruck;

        const existing = findSessionForSlot(slotDate, bookingTruckId);
        if (existing && !['CANCELLED', 'REJECTED'].includes(String(existing.status || '').toUpperCase())) {
            if (existing.status === 'APPROVED') return copy.errors?.slotApproved || DEFAULT_SCHEDULE_COPY.errors.slotApproved;
            return copy.errors?.slotBooked || DEFAULT_SCHEDULE_COPY.errors.slotBooked;
        }

        const midPoint = new Date(slotDate.getTime() + (TRAINING_SLOT_DURATION_MINUTES / 2) * 60000);
        const conflictMid = findSessionForSlot(midPoint, bookingTruckId);
        if (conflictMid) {
            if ((conflictMid.status || '').toUpperCase() === 'APPROVED') {
                return copy.errors?.overlapApproved || DEFAULT_SCHEDULE_COPY.errors.overlapApproved;
            }
            return copy.errors?.overlap || DEFAULT_SCHEDULE_COPY.errors.overlap;
        }

        const overlappingOwnSession = sessions.find(session => {
            const sessionStatus = (session.status || '').toUpperCase();
            if (sessionStatus === 'CANCELLED' || sessionStatus === 'REJECTED') return false;
            if (session.userId !== user.id) return false;
            return slotDate >= session.start && slotDate < session.end;
        });

        if (overlappingOwnSession) {
            return copy.errors?.ownOverlap || DEFAULT_SCHEDULE_COPY.errors.ownOverlap;
        }

        const activeUserId = user?.id;
        if (activeUserId) {
            const dayKey = slotDate.toISOString().split('T')[0];
            const sessionsForDay = sessions.filter(session => {
                if (session.userId !== activeUserId) return false;
                const sessionStatus = (session.status || '').toUpperCase();
                if (sessionStatus === 'CANCELLED' || sessionStatus === 'REJECTED') return false;
                const sessionDayKey = new Date(session.start).toISOString().split('T')[0];
                if (sessionDayKey !== dayKey) return false;
                return session.type === bookingType;
            });

            if (bookingType === 'SKILL' && sessionsForDay.length >= 2) {
                return copy.errors?.skillLimit || DEFAULT_SCHEDULE_COPY.errors.skillLimit;
            }

            if (bookingType === 'ROAD' && sessionsForDay.length >= 1) {
                return copy.errors?.roadLimit || DEFAULT_SCHEDULE_COPY.errors.roadLimit;
            }
        }

        return '';
    };

    const handleSlotSelect = (slotDate, truck) => {
        if (bookingLoading) return;
        setError('');
        setSuccess('');

        const isSameSlot = selectedSlot && selectedTruckId === truck.id && slotDate.getTime() === selectedSlot.getTime();
        const validationError = validateSlot(slotDate, truck);
        if (validationError) {
            setError(validationError);
            return;
        }

        if (isSameSlot) {
            handleBookSession(slotDate, truck);
            return;
        }

        setSelectedTruckId(truck.id);
        setSelectedSlot(slotDate);
    };

    const handleBookSession = async (slotToBook = null, truck = null) => {
        const slot = slotToBook || selectedSlot;
        setError('');
        setSuccess('');
        const selectedTruck = truck || trucks.find(item => item.id === selectedTruckId);
        const validationError = validateSlot(slot, selectedTruck);
        if (validationError) {
            setError(validationError);
            return;
        }
        try {
            setBookingLoading(true);
            await training.bookSession({
                type: resolveTruckType(selectedTruck),
                start: slot.toISOString(),
                durationMinutes: TRAINING_SLOT_DURATION_MINUTES,
                truckId: selectedTruck?.id,
            });
            setSuccess(
                formatMessage(
                    copy.messages?.booked || DEFAULT_SCHEDULE_COPY.messages.booked,
                    { time: formatDateTimeDisplay(slot), status: pendingStatusNote }
                )
            );
            setSelectedSlot(null);
            await refreshSessions();
        } catch (err) {
            console.error('Booking failed:', err);
            setError(err?.message || copy.errors?.bookingFailed || DEFAULT_SCHEDULE_COPY.errors.bookingFailed);
        } finally {
            setBookingLoading(false);
        }
    };

    const changeWeek = (offset) => {
        const newDate = new Date(weekStart);
        newDate.setDate(newDate.getDate() + offset);
        setWeekStart(toWeekStart(newDate));
    };

    const now = new Date();
    const focusedSessionStatusMeta = focusedSession ? getSessionStatusMeta(focusedSession.status) : null;
    const focusedSessionStatusClasses = focusedSession ? getStatusBadgeClasses(focusedSession.status) : '';

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-50/95 px-3 py-4 sm:px-6 sm:py-8">
            <div className="mx-auto w-full max-w-[1440px] overflow-hidden rounded-2xl bg-white shadow-2xl shadow-slate-900/10 ring-1 ring-slate-200">
                <div className="p-3 sm:p-8">
                    <div className="flex items-start justify-between mb-6">
                        <div>
                            <h2 className="text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">{copy.header?.title || DEFAULT_SCHEDULE_COPY.header.title}</h2>
                        </div>
                        <button type="button" onClick={onClose} className="inline-flex shrink-0 items-center gap-2 rounded-2xl bg-slate-800 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-900" aria-label="Back to dashboard"><span className="text-lg leading-none" aria-hidden="true">←</span> Back</button>
                    </div>

                    {(!loadingTrucks && trucks.length === 0) && (
                        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 text-yellow-700 text-sm rounded">
                            {copy.messages?.noTrucks || DEFAULT_SCHEDULE_COPY.messages.noTrucks}
                        </div>
                    )}


                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded">
                            {error}
                        </div>
                    )}
                    {success && (
                        <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 text-sm rounded">
                            {success}
                        </div>
                    )}
                    <div className="mb-4 flex items-center justify-between rounded-xl border border-slate-200 bg-white p-2 shadow-sm">
                        <div className="flex w-full overflow-hidden rounded-lg border border-slate-200 sm:w-auto">
                            <button
                                onClick={() => changeWeek(-7)}
                                className="border-r border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                            >
                                {copy.weekNav?.previous || DEFAULT_SCHEDULE_COPY.weekNav.previous}
                            </button>
                            <button
                                onClick={() => changeWeek(7)}
                                className="px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                            >
                                {copy.weekNav?.next || DEFAULT_SCHEDULE_COPY.weekNav.next}
                            </button>
                        </div>
                    </div>

                    <div className="space-y-4">
                        {loadingTrucks ? <div className="rounded-xl border border-slate-200 bg-white px-4 py-12 text-center text-sm text-slate-500">Loading trucks…</div> : trucks.map((truck, index) => <StudentTruckWeek key={truck.id} truck={truck} index={index} weekDays={weekDays} now={now} selectedSlot={selectedSlot} selectedTruckId={selectedTruckId} loading={loadingSessions || loadingTrucks || bookingLoading} resolveTruckType={resolveTruckType} findSessionForSlot={findSessionForSlot} findApprovedSessionForSlot={findApprovedSessionForSlot} getStatusBadgeClasses={getStatusBadgeClasses} getSessionStatusMeta={getSessionStatusMeta} onSlotSelect={handleSlotSelect} onSessionClick={handleSessionClick} />)}
                    </div>
                    <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-xs font-medium text-slate-600"><span className="flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-full bg-green-500" />Approved</span><span className="flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-full bg-amber-400" />Pending</span><span className="flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-full bg-red-500" />Rejected</span><span className="flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-full bg-slate-400" />Cancelled</span></div>

                </div>
            </div>

            {showSessionDetails && focusedSession && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4">
                    <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-slate-900">{copy.session?.detailsHeading || DEFAULT_SCHEDULE_COPY.session.detailsHeading}</h3>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowSessionDetails(false);
                                    setFocusedSession(null);
                                }}
                                className="text-xl leading-none text-slate-400 hover:text-slate-600"
                                aria-label={copy.session?.closeDetailsAria || DEFAULT_SCHEDULE_COPY.session.closeDetailsAria}
                            >
                                ×
                            </button>
                        </div>

                        <div className="space-y-3 text-sm text-slate-600">
                            <div>
                                <span className="font-medium text-slate-500">{copy.session?.studentLabel || DEFAULT_SCHEDULE_COPY.session.studentLabel}</span>
                                <p className="text-slate-700 mt-0.5">{focusedSession.userName || (copy.table?.reserved || DEFAULT_SCHEDULE_COPY.table.reserved)}</p>
                            </div>

                            <div className="flex items-center gap-2">
                                <span className="font-medium text-slate-500">{copy.session?.typeLabel || DEFAULT_SCHEDULE_COPY.session.typeLabel}</span>
                                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${focusedSession.type === 'ROAD' ? 'bg-blue-100 text-blue-700 border border-blue-200' : 'bg-indigo-100 text-indigo-700 border border-indigo-200'}`}>
                                    {focusedSession.type === 'ROAD'
                                        ? (copy.types?.roadFull || DEFAULT_SCHEDULE_COPY.types.roadFull)
                                        : (copy.types?.skillFull || DEFAULT_SCHEDULE_COPY.types.skillFull)}
                                </span>
                            </div>

                            <div>
                                <span className="font-medium text-slate-500">{copy.session?.timeLabel || DEFAULT_SCHEDULE_COPY.session.timeLabel}</span>
                                <p className="text-slate-700 mt-0.5">{formatTimeRange(focusedSession.start, focusedSession.end)}</p>
                            </div>

                            <div>
                                <span className="font-medium text-slate-500">{copy.session?.truckLabel || DEFAULT_SCHEDULE_COPY.session.truckLabel}</span>
                                <p className="text-slate-700 mt-0.5">{focusedSession.truck?.displayName || focusedSession.truck?.name || (copy.session?.assignedByInstructor || DEFAULT_SCHEDULE_COPY.session.assignedByInstructor)}</p>
                            </div>

                            <div className="flex items-center gap-2">
                                <span className="font-medium text-slate-500">{copy.session?.statusLabel || DEFAULT_SCHEDULE_COPY.session.statusLabel}</span>
                                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${focusedSessionStatusClasses}`}>
                                    {focusedSessionStatusMeta?.icon} {focusedSessionStatusMeta?.label}
                                </span>
                            </div>
                        </div>

                        {(() => {
                            const normalizedStatus = (focusedSession.status || '').toUpperCase();
                            const isOwnSession = focusedSession.userId === user.id;
                            const canCancel = isOwnSession && ['PENDING', 'APPROVED'].includes(normalizedStatus);
                            if (!canCancel) {
                                return null;
                            }
                            return (
                                <div className="mt-6">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (confirmDialog(copy.session?.cancelConfirm || DEFAULT_SCHEDULE_COPY.session.cancelConfirm)) {
                                                handleCancelSession(focusedSession.id);
                                            }
                                        }}
                                        disabled={sessionActionLoading}
                                        className="w-full px-4 py-2 rounded-lg bg-red-100 text-red-700 text-sm font-semibold hover:bg-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {sessionActionLoading
                                            ? (copy.session?.cancellingButton || DEFAULT_SCHEDULE_COPY.session.cancellingButton)
                                            : (copy.session?.cancelButton || DEFAULT_SCHEDULE_COPY.session.cancelButton)}
                                    </button>
                                    <p className="text-xs text-slate-500 mt-2 text-center">
                                        {copy.session?.cancelNotice || DEFAULT_SCHEDULE_COPY.session.cancelNotice}
                                    </p>
                                </div>
                            );
                        })()}
                    </div>
                </div>
            )}
        </div>
    );
}

function StudentTruckWeek({
    truck,
    index,
    weekDays,
    now,
    selectedSlot,
    selectedTruckId,
    loading,
    resolveTruckType,
    findSessionForSlot,
    findApprovedSessionForSlot,
    getStatusBadgeClasses,
    getSessionStatusMeta,
    onSlotSelect,
    onSessionClick,
}) {
    const type = resolveTruckType(truck);
    const accent = type === 'ROAD' ? 'bg-blue-500' : index % 2 ? 'bg-violet-500' : 'bg-emerald-500';

    return <article className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <header className="flex items-center gap-3 border-b border-slate-200 px-4 py-4 sm:px-5">
            <span className={`h-3 w-7 -skew-x-12 ${accent}`} aria-hidden="true" />
            <h3 className="truncate text-lg font-bold text-slate-900 sm:text-xl">{truck.displayName || truck.name}</h3>
            <span className="rounded-md border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-600">{type === 'ROAD' ? 'Road' : 'Skills'}</span>
        </header>
        <div>
            <table className="w-full table-fixed border-collapse">
                <colgroup><col className="w-[54px] sm:w-[84px]" /><col span="7" /></colgroup>
                <thead className="bg-slate-50"><tr><th className="border-r border-slate-200 px-3 py-3 text-left text-xs font-semibold uppercase text-slate-500">Time</th>{weekDays.map(day => <th key={day.toISOString()} className="border-r border-dashed border-slate-200 px-1 py-2 text-center last:border-r-0"><span className="block text-[10px] font-bold text-slate-800 sm:text-xs">{day.toLocaleDateString(undefined, { weekday: 'short' }).toUpperCase()}</span><span className="mt-0.5 block text-[10px] font-medium text-slate-500 sm:text-xs">{day.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span></th>)}</tr></thead>
                <tbody>{TRAINING_TIME_SLOTS.map((slot, slotIndex) => <tr key={slot.minutes} className="h-12 border-t border-slate-100 sm:h-14"><th scope="row" className="border-r border-slate-200 px-2 text-right text-[10px] font-medium text-slate-500 sm:px-3 sm:text-xs">{slotIndex % 2 === 0 ? <TimeLabel label={slot.label.split(' - ')[0]} /> : null}</th>{weekDays.map(day => {
                    const slotDate = combineDateAndMinutes(day, slot.minutes);
                    const session = findSessionForSlot(slotDate, truck.id);
                    const isStart = session && sessionStartsInSlot(session, slotDate);
                    const isSelected = selectedSlot && selectedTruckId === truck.id && selectedSlot.getTime() === slotDate.getTime();
                    const isPast = slotDate < now;
                    const isReserved = findApprovedSessionForSlot(slotDate, truck.id);
                    if (session) return <td key={day.toISOString()} className="border-r border-dashed border-slate-200 p-1 align-top last:border-r-0">{isStart ? <button type="button" onClick={() => onSessionClick(session)} className={`h-full min-h-10 w-full overflow-hidden rounded-md border px-1.5 py-1 text-left text-[10px] font-semibold shadow-sm sm:px-2 sm:text-xs ${getStatusBadgeClasses(session.status)}`}><span className="flex items-start justify-between gap-1"><span className="truncate">{session.type === 'ROAD' ? 'Road training' : 'Skills training'}</span><span className="shrink-0">{getSessionStatusMeta(session.status).icon}</span></span><span className="mt-0.5 block truncate text-[9px] font-medium opacity-80 sm:text-[10px]">{formatTimeRange(session.start, session.end)}</span></button> : <div className={`h-full min-h-10 rounded-md opacity-50 ${getStatusBadgeClasses(session.status).split(' ')[0]}`} />}</td>;
                    return <td key={day.toISOString()} className="border-r border-dashed border-slate-200 p-1 last:border-r-0"><button type="button" disabled={isPast || loading || isReserved} onClick={() => onSlotSelect(slotDate, truck)} className={`h-full min-h-10 w-full rounded-md text-xs transition-colors ${isSelected ? 'bg-blue-100 text-blue-700 ring-1 ring-blue-300' : 'text-slate-300 hover:bg-blue-50'} disabled:cursor-not-allowed disabled:opacity-40`} aria-label={`Select ${slot.label} on ${truck.displayName || truck.name}`}>{isSelected ? 'Selected' : '—'}</button></td>;
                })}</tr>)}</tbody>
            </table>
        </div>
    </article>;
}

function TimeLabel({ label }) {
    const [time, meridiem] = label.split(' ');
    return <><span className="block">{time}</span><span className="block">{meridiem}</span></>;
}
